import { Box, IconButton, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { InfoOutlined, PersonAdd, PersonRemove, Remove } from '@mui/icons-material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { MemberAdvancedSearchAddProfile } from 'src/modules/member/components/memberAdvancedSearchAddProfile';
import { MemberProfileData } from 'src/modules/member/models/member';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { PaymentMethodEnum } from 'src/shared/models/enums/paymentMethod.enum';
import { FeePreview } from 'src/modules/district/models/districtFee';
import { AssociationFeeRequest } from '../models/associationFeeRequest';

/** Nivåernas delar bakom en summa. Visas i tooltipen på den slutliga avgiften. */
type FeeParts = {
  country?: number;
  district?: number;
  local?: number;
};

type AssociationFeeTableProps = {
  paymentMethod: PaymentMethodEnum;
  readOnlyMode: boolean;
  /** Visar autogirokolumnen. */
  autogiroFee?: boolean;
  /** Vänmedlem är ett eget belopp och visas bara vid fördelningsbaserad avgift. */
  showFriendFee?: boolean;
  /**
   * Rubrik över beloppskolumnen. Vid fördelningsbaserad avgift säger den vilken nivå beloppen
   * tillhör, annars bara Avgift.
   */
  amountHeader: string;
  /** Slutlig avgift per rad. Utelämnas när fördelningsbaserad avgift är av, och då visas ingen sådan kolumn. */
  feePreview?: FeePreview;
  /**
   * Fördelningsbaserad avgift. Autogirofälten låses då, eftersom aviseringarna ännu inte räknar
   * ihop nivåernas autogirobelopp. Ett ifyllt belopp skulle alltså se ut att gälla utan att göra
   * det.
   */
  distributedFee?: boolean;
  /**
   * Profilerna styrs av förbundet och kan inte tas bort här. Gäller distriktet och föreningen
   * i fördelningsläget, där alla tre nivåerna måste ha samma profiler för att medlemmen inte
   * ska tappa en nivås del.
   */
  lockedProfiles?: boolean;
  openAddprofile: boolean;
  setOpenAddprofile: (open: boolean) => void;
};

/**
 * Avgiftsflikens belopp i en tabell med fasta kolumner. Aviseringsmetodens rader och profilraderna
 * delar kolumner och rubrikrad, i stället för att ligga i var sitt rutmönster.
 * Fälten är samma ControlledTextField med samma name som tidigare, så formulärets datamodell,
 * validering och sparning är oförändrade.
 */
export const AssociationFeeTable = (props: AssociationFeeTableProps) => {
  const {
    paymentMethod,
    readOnlyMode,
    autogiroFee = undefined,
    showFriendFee = false,
    amountHeader,
    feePreview = undefined,
    distributedFee = false,
    lockedProfiles = false,
    openAddprofile,
    setOpenAddprofile,
  } = props;

  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);

  // Kunden kan döpa om vänmedlem i modulinställningarna. Utan eget namn heter det Vänmedlem.
  const friendLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.NewFriendLabel, t('association:feePreviewFriend'));

  const { control } = useFormContext<AssociationFeeRequest>();

  const familySteps = useFieldArray<AssociationFeeRequest, 'familySteps', 'id'>({ control, name: 'familySteps' });
  const profiles = useFieldArray<AssociationFeeRequest, 'feeBasedOnProfile', 'memberProfileItem_id'>({
    control,
    name: 'feeBasedOnProfile',
  });

  // Autogiro-avgift per profil går bara att ange för Huvudmedlem + familjemedlem och för
  // Avgift baserad på profil, samma regel som tidigare.
  const showProfileAutogiroFee =
    !!autogiroFee && (paymentMethod === PaymentMethodEnum.MainAndFamily || paymentMethod === PaymentMethodEnum.FeeBasedOnProfile);

  const showTotals = !!feePreview;

  // Delarna bakom varje summa, hämtade ur samma förhandsvisning som summan själv.
  const mainParts: FeeParts = { country: feePreview?.countryFee, district: feePreview?.districtFee, local: feePreview?.localFee };
  const familyParts: FeeParts = {
    country: feePreview?.countryFeeFamily,
    district: feePreview?.districtFeeFamily,
    local: feePreview?.localFeeFamily,
  };
  const friendParts: FeeParts = {
    country: feePreview?.countryFeeFriend,
    district: feePreview?.districtFeeFriend,
    local: feePreview?.localFeeFriend,
  };
  const showRemoveColumn = !readOnlyMode && !lockedProfiles;

  // Profilgrupp, profil, belopp och sedan de valfria kolumnerna.
  const columnCount = 3 + (autogiroFee ? 1 : 0) + (showTotals ? 1 : 0) + (showRemoveColumn ? 1 : 0);

  const handleProfileAdded = (newProfiles: MemberProfileData[]) => {
    const existingIds: number[] = profiles.fields.map((p) => p.memberProfileItem_id);
    const filteredNewProfiles = newProfiles.filter((profile) => !existingIds.includes(profile.memberProfileItem_id));

    filteredNewProfiles.forEach((profile) => {
      profiles.append({
        memberProfileItem_id: profile.memberProfileItem_id,
        profileGroup: profile.groupName,
        profile: profile.itemName,
        fee: 0,
        feeAutogiro: showProfileAutogiroFee ? 0 : undefined,
      });
    });

    setOpenAddprofile(false);
  };

  const amountCell = (name: string, disabled = false) => (
    <ControlledTextField
      disabled={readOnlyMode || disabled}
      label=''
      type='number'
      name={name}
      size='small'
      InputProps={{
        endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
      }}
    />
  );

  /** Nivåernas delar bakom en summa, som innehåll i en tooltip. */
  const partsTooltip = (parts: FeeParts, total: number) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 2, rowGap: 0.25 }}>
      <span>{t('association:feeTableCountryPart')}</span>
      <span style={{ textAlign: 'right' }}>
        {parts.country ?? 0} {currency}
      </span>

      <span>{t('association:feeTableDistrictPart')}</span>
      <span style={{ textAlign: 'right' }}>
        {parts.district ?? 0} {currency}
      </span>

      <span>{t('association:feeTableLocalPart')}</span>
      <span style={{ textAlign: 'right' }}>
        {parts.local ?? 0} {currency}
      </span>

      <span style={{ fontWeight: 600, borderTop: '1px solid currentColor', paddingTop: 2 }}>{t('association:feeRowTotalHeader')}</span>
      <span style={{ fontWeight: 600, textAlign: 'right', borderTop: '1px solid currentColor', paddingTop: 2 }}>
        {total} {currency}
      </span>
    </Box>
  );

  const totalCell = (total?: number, parts?: FeeParts) => (
    <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
      {total === undefined || total === null ? (
        ''
      ) : (
        <Tooltip title={parts ? partsTooltip(parts, total) : t('association:feeRowTotalInfo')}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', cursor: 'help' }}>
            {total} {currency}
          </Typography>
        </Tooltip>
      )}
    </TableCell>
  );

  /** En rad med etikett, ett beloppsfält, ett autogirofält och en valfri summa. */
  const feeRow = (key: string, label: string, name: string, autogiroName?: string, total?: number, action?: JSX.Element, parts?: FeeParts) => (
    <TableRow key={key}>
      <TableCell colSpan={2}>{label}</TableCell>
      <TableCell>{amountCell(name)}</TableCell>
      {showTotals && totalCell(total, parts)}
      {autogiroFee && <TableCell>{autogiroName ? amountCell(autogiroName, distributedFee) : ''}</TableCell>}
      {showRemoveColumn && <TableCell align='right'>{action ?? ''}</TableCell>}
    </TableRow>
  );

  const familyStepLabel = (member: string | undefined, index: number) => {
    if (member === 'mainMember') return t('association:paymentMethodFieldLabels.familyStepMainMember');
    return t('association:paymentMethodFieldLabels.familyStepfamilyMember', { count: index });
  };

  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size='small' sx={{ minWidth: 620, '& td': { fontSize: '1rem' } }}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={2} sx={{ width: '32%' }}>
                {t('association:feeTableSubject')}
              </TableCell>
              <TableCell sx={{ width: '20%' }}>{amountHeader}</TableCell>
              {/* Summan räknas på den vanliga avgiften, inte på autogirobeloppet, och står
                  därför bredvid den kolumn den hör till. */}
              {showTotals && (
                <TableCell align='right' sx={{ width: '20%' }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    {t('association:feeRowTotalHeader')}
                    <Tooltip title={t('association:feeRowTotalInfo')}>
                      <InfoOutlined sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
              )}
              {autogiroFee && (
                <TableCell sx={{ width: '20%' }}>
                  {distributedFee ? t('association:autogiroFeeLabelDistributed') : t('association:autogiroFeeLabel')}
                </TableCell>
              )}
              {showRemoveColumn && <TableCell sx={{ width: '8%' }} />}
            </TableRow>
          </TableHead>

          <TableBody>
            {paymentMethod === PaymentMethodEnum.SameFeeForAll &&
              feeRow(
                'sameFeeForAll',
                t('association:feeTableMember'),
                'sameFeeForAll',
                'sameFeeForAllAutogiro',
                feePreview?.total,
                undefined,
                mainParts,
              )}

            {paymentMethod === PaymentMethodEnum.MainAndFamily && (
              <>
                {feeRow(
                  'mainMember',
                  t('association:feePreviewMainMember'),
                  'mainAndFamily.feeMainMember',
                  'mainAndFamily.feeMainMemberAutogiro',
                  feePreview?.total,
                  undefined,
                  mainParts,
                )}
                {feeRow(
                  'familyMember',
                  t('association:feePreviewFamilyMember'),
                  'mainAndFamily.feeFamilyMember',
                  'mainAndFamily.feeFamilyMemberAutogiro',
                  feePreview?.totalFamily,
                  undefined,
                  familyParts,
                )}
              </>
            )}

            {paymentMethod === PaymentMethodEnum.FamilySteps &&
              familySteps.fields.map((field, index) => {
                const isFixed = field.member === 'mainMember' || field.member === 'familyMember1';
                const isMainMember = field.member === 'mainMember';
                const isLast = index === familySteps.fields.length - 1;

                let action: JSX.Element | undefined;

                if (!readOnlyMode && isMainMember) {
                  action = (
                    <Tooltip title={t('association:paymentMethodFieldLabels.addFamilyMember')} placement='left'>
                      <IconButton
                        onClick={() => {
                          const currentCount = familySteps.fields.filter((f) => f.member?.startsWith('familyMember')).length;
                          familySteps.append({ member: `familyMember${currentCount + 1}`, fee: undefined, feeAutogiro: undefined });
                        }}
                      >
                        <PersonAdd color='primary' fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  );
                }

                if (!readOnlyMode && !isFixed && isLast) {
                  action = (
                    <Tooltip title={t('association:paymentMethodFieldLabels.removeFamilyMember')} placement='left'>
                      <IconButton color='error' onClick={() => familySteps.remove(index)}>
                        <PersonRemove fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  );
                }

                return feeRow(
                  field.id,
                  familyStepLabel(field.member, index),
                  `familySteps.${index}.fee`,
                  `familySteps.${index}.feeAutogiro`,
                  index === 0 ? feePreview?.total : undefined,
                  action,
                  index === 0 ? mainParts : undefined,
                );
              })}

            {paymentMethod === PaymentMethodEnum.OneFeeForHousehold &&
              feeRow(
                'household',
                t('association:paymentMethodFieldLabels.oneFeeForHousehold'),
                'oneFeeForHousehold',
                'oneFeeForHouseholdAutogiro',
                feePreview?.total,
                undefined,
                mainParts,
              )}

            {showFriendFee && feeRow('friend', friendLabel, 'feeFriend', 'feeFriendAutogiro', feePreview?.totalFriend, undefined, friendParts)}

            {profiles.fields.length > 0 && (
              <>
                {/* Egen rad för luften mellan metodens rader och profilerna. Padding på cellerna
                    räcker inte, tabellcellernas egen padding tar över. */}
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ border: 0, height: 36, p: 0 }} />
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: '16%' }}>
                    <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 600 }}>
                      {t('association:profileGroup')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: '16%' }}>
                    <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 600 }}>
                      {t('association:profile')}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={columnCount - 2} />
                </TableRow>
              </>
            )}

            {profiles.fields.map((field, index) => (
              <TableRow key={field.memberProfileItem_id}>
                <TableCell>{field.profileGroup}</TableCell>
                <TableCell>{field.profile}</TableCell>
                <TableCell>{amountCell(`feeBasedOnProfile.${index}.fee`)}</TableCell>
                {showTotals &&
                  (() => {
                    const profilePreview = feePreview?.profiles?.find((x) => x.memberProfileItemId === field.memberProfileItem_id);

                    return totalCell(profilePreview?.total, {
                      country: profilePreview?.countryFee,
                      district: profilePreview?.districtFee,
                      local: profilePreview?.localFee,
                    });
                  })()}
                {autogiroFee && (
                  <TableCell>{showProfileAutogiroFee ? amountCell(`feeBasedOnProfile.${index}.feeAutogiro`, distributedFee) : ''}</TableCell>
                )}
                {showRemoveColumn && (
                  <TableCell align='right'>
                    <IconButton color='error' onClick={() => profiles.remove(index)}>
                      <Remove fontSize='small' />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {showTotals && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5 }}>
          {t('association:feeTableProfileNote')}
        </Typography>
      )}

      <GenericDialog
        open={openAddprofile}
        showDialogActions={false}
        dialogTitle={t('association:addProfile')}
        maxWidth='sm'
        dialogContent={
          <Box mb={2}>
            <MemberAdvancedSearchAddProfile
              propName='addProfile'
              callback={(val) => {
                handleProfileAdded(val);
              }}
              closeEdit={() => setOpenAddprofile(false)}
            />
          </Box>
        }
        onClose={() => setOpenAddprofile(false)}
        onSave={() => ConsoleHelper.log()}
      />
    </>
  );
};

export default AssociationFeeTable;
